/**
 * @file checkbox.tsx
 * @description Checkbox-примитив на base-ui (phase08.5).
 *
 * `Checkbox.Root` у base-ui рендерит `<span role="checkbox">`, а не `<button>`:
 * у span'а `display: inline`, поэтому `size-*` на нём не работает и галочка
 * получается 0×0 — невидимая и некликабельная (дефект QA 2026-09-21, ломало
 * ВСЕХ потребителей примитива, не только склад). Поэтому `inline-flex` на
 * корне — часть контракта примитива, а не украшение: снимать его нельзя.
 */

import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import { Check, Minus } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

export const Checkbox = React.forwardRef<
  HTMLSpanElement,
  React.ComponentProps<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    data-slot="checkbox"
    className={cn(
      // inline-flex + align-middle: иначе span схлопывается в 0×0 и «съезжает»
      // относительно подписи в строке текста.
      "peer inline-flex shrink-0 cursor-pointer items-center justify-center align-middle",
      "size-4 rounded-sm bg-input outline-none transition-all",
      "[box-shadow:inset_0_0_0_1.5px_var(--outline-variant)]",
      "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
      "data-[checked]:bg-primary data-[checked]:[box-shadow:none] data-[checked]:text-on-primary",
      "data-[unchecked]:hover:[box-shadow:inset_0_0_0_1.5px_var(--primary)]",
      "disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className="flex items-center justify-center text-current"
      render={(props, state) => (
        <span {...props}>
          {state.indeterminate ? (
            <Minus className="size-3" />
          ) : state.checked ? (
            <Check className="size-3" />
          ) : null}
        </span>
      )}
    />
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = "Checkbox";
